import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, ShoppingBag, ShoppingCart, Star, Zap, Clock, Sparkles, Search, Inbox } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { formatRupiah } from "@/lib/dummyData";
import { ProductAPI } from "@/lib/api";
import { useCart } from "@/lib/cart";

const mapProduct = (p) => ({
  id: p.id, name: p.name, category: p.category, image: p.image,
  price: Number(p.price_non_member), memberPrice: Number(p.price_member), stock: p.stock,
});

export default function MemberShop() {
  const navigate = useNavigate();
  const [cat, setCat] = useState("Semua");
  const [q, setQ] = useState("");
  const [products, setProducts] = useState([]);
  const [productCategories, setProductCategories] = useState(["Semua"]);
  const [loaded, setLoaded] = useState(false);
  // Jumlah item keranjang dari konteks bersama (sama dengan badge di navigasi).
  const { count: cartCount, addItem, refreshServer } = useCart();

  useEffect(() => {
    ProductAPI.list().then((rows) => setProducts((rows || []).map(mapProduct))).catch(() => {}).finally(() => setLoaded(true));
    ProductAPI.categories().then((cats) => cats?.length && setProductCategories(cats)).catch(() => {});
    refreshServer();
  }, [refreshServer]);

  const addToCart = async (p) => {
    try {
      await addItem(p, 1);
      toast.success(`${p.name} ditambahkan ke keranjang`, {
        action: { label: "Lihat", onClick: () => navigate("/anggota/keranjang") },
      });
    } catch { toast.error("Gagal menambah ke keranjang"); }
  };

  const filtered = useMemo(() => products.filter((p) =>
    (cat === "Semua" || p.category === cat) && p.name.toLowerCase().includes(q.toLowerCase())
  ), [products, cat, q]);

  return (
    <div className="space-y-7">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3">
        <div>
          <div className="pill-tag bg-amber-warm/15 text-amber-700 mb-3">Eksklusif Anggota</div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-ink tracking-tight">Toko Anggota</h1>
          <p className="text-ink/75 mt-2">Hemat 8-15% dengan harga khusus anggota. Bayar online lewat Midtrans (VA, QRIS, e-wallet, kartu).</p>
        </div>
        <div className="flex items-center gap-3 w-full lg:w-auto">
          <div className="relative flex-1 lg:w-80">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/55" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari produk anggota..." className="pl-9 bg-white border-stone-soft rounded-full" data-testid="member-shop-search" />
          </div>
          {/* Ikon keranjang → keranjang & checkout di ruang anggota */}
          <button
            onClick={() => navigate("/anggota/keranjang")}
            className="relative shrink-0 w-11 h-11 rounded-full bg-forest text-white grid place-items-center hover:bg-forest-dark transition shadow-md"
            aria-label={`Buka keranjang (${cartCount} item)`}
            data-testid="member-cart-icon"
          >
            <ShoppingCart size={18} />
            {cartCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 rounded-full bg-terracotta text-white text-[11px] font-bold grid place-items-center border-2 border-white" data-testid="member-cart-count">
                {cartCount > 99 ? "99+" : cartCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Flash sale banner */}
      <Card className="rounded-3xl bg-terracotta text-cream border-0 p-7 relative overflow-hidden grain-overlay">
        <div className="relative z-10 grid lg:grid-cols-3 gap-6 items-center">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2">
              <Zap className="text-amber-warm" size={18} />
              <span className="text-[10px] uppercase tracking-[0.22em] text-amber-warm font-semibold">Flash Sale 24 Jam</span>
            </div>
            <h2 className="font-display text-2xl lg:text-3xl font-bold mt-2">Hemat hingga <span className="font-editorial italic text-amber-warm">30%</span> untuk anggota emas</h2>
            <p className="text-cream/80 mt-2 text-sm">Berlaku untuk Madu Hutan, Kopi Robusta, dan Beras Premium. Stok terbatas.</p>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {["12", "34", "27"].map((n, i) => (
              <div key={i} className="rounded-2xl bg-white/10 backdrop-blur p-3 text-center border border-white/15">
                <div className="font-display font-bold text-3xl">{n}</div>
                <div className="text-[10px] uppercase tracking-wider text-cream/70 mt-1">{["Jam", "Menit", "Detik"][i]}</div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Categories */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {productCategories.map((c) => (
          <button
            key={c}
            onClick={() => setCat(c)}
            data-testid={`member-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition ${
              cat === c ? "bg-forest text-white" : "bg-white border border-stone-soft text-ink/75 hover:bg-cream"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Grid */}
      {loaded && filtered.length === 0 ? (
        <Card className="rounded-2xl border-stone-soft p-16 text-center">
          <Inbox className="mx-auto text-ink/30" size={32} />
          <h3 className="font-display font-semibold text-ink mt-3">{q || cat !== "Semua" ? "Tidak ada produk yang cocok" : "Belum ada produk"}</h3>
          <p className="text-sm text-ink/55 mt-1">Produk akan muncul di sini setelah tersedia di katalog.</p>
        </Card>
      ) : (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {filtered.map((p) => (
          <Card key={p.id} className="rounded-2xl border-stone-soft overflow-hidden hover-lift" data-testid={`member-product-${p.id}`}>
            <div className="relative aspect-square overflow-hidden bg-cream">
              <img src={p.image} alt={p.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
              <Badge className="absolute top-3 left-3 bg-amber-warm text-ink hover:bg-amber-warm border-0 rounded-full text-[10px] uppercase tracking-wider">
                Harga Anggota
              </Badge>
              <button className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 grid place-items-center hover:bg-white" data-testid={`wishlist-${p.id}`}>
                <Heart size={15} className="text-terracotta" />
              </button>
            </div>
            <div className="p-4">
              <div className="text-[10px] uppercase tracking-wider text-ink/55">{p.category}</div>
              <div className="font-medium text-ink text-sm mt-1 line-clamp-2 min-h-[2.4rem]">{p.name}</div>
              <div className="text-xs text-forest mt-1">Stok {p.stock}</div>
              <div className="mt-3">
                <div className="font-display font-bold text-amber-700">{formatRupiah(p.memberPrice)}</div>
                <div className="text-xs text-ink/55 line-through">{formatRupiah(p.price)}</div>
              </div>
              <Button onClick={() => addToCart(p)} className="w-full mt-3 rounded-full bg-forest hover:bg-forest-dark text-white text-xs h-9" data-testid={`member-add-${p.id}`}>
                <ShoppingBag size={13} /> Tambah ke Keranjang
              </Button>
            </div>
          </Card>
        ))}
      </div>
      )}

      <Card className="rounded-2xl border-stone-soft p-6 bg-cream">
        <div className="flex items-start gap-3">
          <Sparkles className="text-amber-warm shrink-0" size={20} />
          <div>
            <h3 className="font-display font-semibold text-ink">Tahukah Anda?</h3>
            <p className="text-sm text-ink/75 mt-1">
              Setiap pembelanjaan di Toko Anggota turut menghitung sebagai jasa usaha untuk SHU akhir tahun Anda.
              Semakin sering belanja, semakin besar SHU yang Anda terima.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
